// src/MicrosoftLogin.jsx
import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';

const MicrosoftLogin = () => {
  const { instance, accounts, inProgress } = useMsal(); // still used for Support email via Graph
  const [sbSession, setSbSession] = useState(null);
  const [sbLoading, setSbLoading] = useState(true);
  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState('');
  const [openSupport, setOpenSupport] = useState(false);
  const [supportForm, setSupportForm] = useState({
    firstName: '',
    lastName: '',
    companyEmail: '',
  });

  // Supabase session watcher (this is the auth that RLS uses)
  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        const { data } = await supabase.auth.getSession();
        if (isMounted) setSbSession(data.session);

      } finally {
        if (isMounted) setSbLoading(false);
      }
    }

    load();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (isMounted) setSbSession(session);
      if (isMounted) setSbLoading(false);
    });

    return () => {
      isMounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, []);

  // After Supabase OAuth sign-in, redirect to /dashboard
  useEffect(() => {
    if (!sbLoading && sbSession) {
      navigate('/dashboard', { replace: true });
    }
  }, [sbLoading, sbSession, navigate]);

  const handleLogin = async () => {
    // Primary auth path: Supabase Azure OAuth (this produces the JWT used by RLS)
    setErrorMessage('');
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          // Supabase will validate against Auth → URL Configuration
          redirectTo: `${window.location.origin}/dashboard`,
          // Ask Microsoft for permission to read the signed-in person's own
          // profile. Without a scope here Supabase requests the default set,
          // and the provider token that comes back cannot call Graph — which
          // is why the header had no photograph to show. User.Read is the
          // narrowest scope that returns one, and covers only the person
          // signing in, nobody else.
          scopes: 'openid profile email User.Read',
        },
      });
      if (error) throw error;
    } catch (err) {
      console.error('SUPABASE OAUTH FAILED', err);
      setErrorMessage(`Sign-in failed: ${err?.message || err}`);
    }
  };

  const handleOpenSupport = () => setOpenSupport(true);
  const handleCloseSupport = () => {
    setOpenSupport(false);
    setSupportForm({ firstName: '', lastName: '', companyEmail: '' });
    setErrorMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSupportForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    const { firstName, lastName, companyEmail } = supportForm;
    if (!firstName || !lastName || !companyEmail) {
      setErrorMessage('Please fill in all fields.');
      return;
    }
    try {
      let tokenResponse;
      try {
        tokenResponse = await instance.acquireTokenSilent({
          scopes: ['Mail.Send'],
          account: accounts[0],
        });
      } catch {
        tokenResponse = await instance.acquireTokenPopup({
          scopes: ['Mail.Send'],
        });
      }

      const emailPayload = {
        message: {
          subject: 'Support Request from Personal Shopping App',
          body: {
            contentType: 'Text',
            content: `First Name: ${firstName}\nLast Name: ${lastName}\nEmail: ${companyEmail}`,
          },
          toRecipients: [
            { emailAddress: { address: 'ITHelp@llpersonalshopping.com' } },
          ],
        },
      };

      await axios.post(
        'https://graph.microsoft.com/v1.0/me/sendMail',
        emailPayload,
        { headers: { Authorization: `Bearer ${tokenResponse.accessToken}` } }
      );

      alert('Support request sent!');
      handleCloseSupport();
    } catch (err) {
      console.error('Graph sendMail error:', err);
      setErrorMessage(
        `Unable to send support request: ${err.errorCode || err.name || err.message}`
      );
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'url("/background.png") no-repeat center center',
        backgroundSize: 'cover',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '50%',
          maxWidth: 320,
          p: 3,
          bgcolor: 'rgba(255,255,255,0.9)',
          borderRadius: 2,
          boxShadow: 3,
          textAlign: 'center',
        }}
      >
        <img
          src="/logo.png"
          alt="Logo"
          style={{ width: 160, marginBottom: 20 }}
        />


        {inProgress !== InteractionStatus.None ? (
          <>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading…</Typography>
          </>
        ) : sbSession ? (
          <Typography>You are already signed in.</Typography>
        ) : (
          <>
            <Button
              variant="contained"
              onClick={handleLogin}
              startIcon={
                <img
                  src="/ms-logo.png"
                  alt="Microsoft Logo"
                  style={{ width: 20, height: 20 }}
                />
              }
              sx={{
                mb: 2,
                width: '75%',      // full container width
                height: 48,         // custom button height
                fontSize: '1rem',   // adjust text size if needed
                textTransform: 'none',
              }}
            >
              Sign in with Microsoft
            </Button>

            {errorMessage && (
              <Typography color="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Typography>
            )}

            <Typography variant="body2">
              Trouble signing in?{' '}
              <Box
                component="span"
                onClick={handleOpenSupport}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Contact Support
              </Box>
            </Typography>
          </>
        )}

        <Dialog open={openSupport} onClose={handleCloseSupport}>
          <DialogTitle>Contact Support</DialogTitle>
          <Box component="form" onSubmit={handleSupportSubmit}>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="First Name"
                id="support-first-name"
                name="firstName"
                fullWidth
                value={supportForm.firstName}
                onChange={handleInputChange}
              />
              <TextField
                margin="dense"
                label="Last Name"
                id="support-last-name"
                name="lastName"
                fullWidth
                value={supportForm.lastName}
                onChange={handleInputChange}
              />
              <TextField
                margin="dense"
                label="Company Email"
                id="support-company-email"
                name="companyEmail"
                type="email"
                fullWidth
                value={supportForm.companyEmail}
                onChange={handleInputChange}
              />
              {errorMessage && (
                <Typography color="error" sx={{ mt: 1 }}>
                  {errorMessage}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseSupport}>Cancel</Button>
              <Button type="submit" variant="contained">
                Submit
              </Button>
            </DialogActions>
          </Box>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MicrosoftLogin;
